/**
 * F31 (Wave 2B) — admin resolve-skill-dispute dialog.
 *
 * Radio-card final-result picker (winning side: 'a' / 'b' / 'draw' when the
 * sport supports it) + mandatory resolution reason ≥10 chars (validated against
 * `ResolveMatchDisputeInputSchema`). Posts via the parent's `onResolve` which
 * calls `adminSkillDisputesApi.resolveDispute`.
 *
 * Gated by the parent page on `skill_disputes:resolve`. Composes F02 primitives
 * only (Modal / FormField / SegmentedControl / Textarea / Button / Banner).
 */
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, SegmentedControl, Textarea } from '@bravemove/ui-web';
import {
  ResolveMatchDisputeInputSchema,
  type ResolveMatchDisputeInput,
  type WinningSide,
} from '@bravemove/types';

export interface ResolveSkillDisputeDialogProps {
  readonly open: boolean;
  readonly supportsDraws: boolean;
  readonly onResolve: (input: ResolveMatchDisputeInput) => Promise<void>;
  readonly onClose: () => void;
  readonly errorMessage?: string | null;
}

export function ResolveSkillDisputeDialog({
  open,
  supportsDraws,
  onResolve,
  onClose,
  errorMessage,
}: ResolveSkillDisputeDialogProps) {
  const { t } = useI18n();
  const [winningSide, setWinningSide] = useState<WinningSide>('a');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setWinningSide('a');
    setReason('');
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Dedicated side-labelled keys (TGS-10): the previous `skill:outcome.result.
  // win`/`loss` keys EXIST in the shared namespace as "Won"/"Lost", so the
  // 'Side A wins'/'Side B wins' defaultValues never applied and the picker was
  // ambiguous about WHICH side each option resolves in favour of.
  const options = useMemo(() => {
    const base: { value: WinningSide; label: string }[] = [
      {
        value: 'a',
        label: t('admin:skill.disputes.resolve.side_a_wins', { defaultValue: 'Side A wins' }),
      },
      {
        value: 'b',
        label: t('admin:skill.disputes.resolve.side_b_wins', { defaultValue: 'Side B wins' }),
      },
    ];
    if (supportsDraws) {
      base.push({ value: 'draw', label: t('skill:outcome.result.draw', { defaultValue: 'Draw' }) });
    }
    return base;
  }, [supportsDraws, t]);

  const payload: ResolveMatchDisputeInput = useMemo(
    () => ({ winningSide, reason }),
    [winningSide, reason],
  );
  const valid = useMemo(() => ResolveMatchDisputeInputSchema.safeParse(payload).success, [payload]);

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await onResolve(payload);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('skill:admin.skill_disputes.resolve.title', { defaultValue: 'Resolve dispute' })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="resolve-skill-dispute-cancel"
          >
            {t('skill:outcome.dialog.confirm.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            disabled={!valid || submitting}
            data-testid="resolve-skill-dispute-submit"
          >
            {t('skill:admin.skill_disputes.resolve.submit', { defaultValue: 'Resolve' })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="resolve-skill-dispute-dialog">
        {errorMessage ? <Banner tone="danger">{errorMessage}</Banner> : null}
        <FormField
          label={t('skill:admin.skill_disputes.resolve.final_result_label', {
            defaultValue: 'Final result',
          })}
          required
        >
          <SegmentedControl<WinningSide>
            value={winningSide}
            onChange={(v) => setWinningSide(v)}
            options={options}
            fullWidth
            aria-label={t('skill:admin.skill_disputes.resolve.final_result_label', {
              defaultValue: 'Final result',
            })}
          />
        </FormField>
        <FormField
          label={t('skill:admin.skill_disputes.resolve.reason_label', {
            defaultValue: 'Resolution reason',
          })}
          required
        >
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('skill:admin.skill_disputes.resolve.reason_placeholder', {
              defaultValue: 'Explain the decision (at least 10 characters)',
            })}
            rows={4}
            maxLength={500}
            data-testid="resolve-skill-dispute-reason"
          />
        </FormField>
      </div>
    </Modal>
  );
}
